<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from "vue";
import { SortItem } from "vuetify/lib/components/VDataTable/composables/sort";
import { useApiClient } from "@/api/client";
import { useExplorerStore } from "@/modules/explorer/store";
import { useDashboardStore } from "@/modules/dashboards/store";
import { HierarchyKey, useHierarchyStore } from "@/stores/hierarchyStore";
import { useAbortController } from "@/modules/explorer/composables/useAbortController";
import { assert, useCloned, useResizeObserver } from "@vueuse/core";
import { Branch, useExcludedBranch } from "./useExcludedBranch";
import { t } from "@/plugins/i18n";
import { formatNumber, processedDocument } from "chartjs-graph";
import { debounce } from "vue-debounce";
import {
	filterHasInvalidDataSource,
	hierarchyLoadErrorMessage,
} from "@/modules/explorer/components/FilterBar/filterUtils";

import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import ExcludedHierarchyFilterValueDTO = App.Data.Explorer.Filter.ExcludedHierarchyFilterValueDTO;
import QuickFilterNodeDTO = App.Data.Explorer.Filter.QuickFilterNodeDTO;
import CustomTableHeader from "@/modules/explorer/components/QuickFilter/CustomTableHeader.vue";
import QuickFilterComparisonValueCell from "@/modules/explorer/components/QuickFilter/QuickFilterComparisonValueCell.vue";
import {
	BUDGET_COMPARISON_LABEL,
	getComparisonCellState,
	getUnavailableComparisonLabels,
	hasDocumentChanged,
} from "@/modules/explorer/components/QuickFilter/quickFilterTools";

/**
 * The hierarchical version of the Quick Filter Menu (QFM).
 *
 * As the backend/legacy hierarchy structure is quite clunky, some of the I/O is a bit rough.
 * More can be read in `useExcludedBranch.ts`, but to keep it short:
 * The `level` + `label` structure is a pain to use, so we use `Branch`es.
 * These need to be converted from/to the old structure whenever changes are made,
 * which is why the reactivity in this component can be a bit tricky
 *
 * ---
 *
 * Also this file is quite big, with no realistic way to really split it up (that I'm aware of).
 * This means that the `<script>` part doesn't follow the classic `ref -> computed -> functions` structure.
 * Instead its split up per category/responsibilty
 */

type QuickFilterNodeWithPath = QuickFilterNodeDTO & {
	branch: Branch;
};

// Mirrors DocumentDateComparisonDTO::getComparisonPeriodLabels() on the backend, minus the primary
// label. Ordered oldest-to-newest (Y-7, ..., Y-1), then custom periods, then budget, so it reads
// left-to-right the same way it would chronologically, ending right before the current-year column.
//
// Dashboards (isExplorer false) have no comparison concept at all — DashboardDO has no
// date_comparison field — and explorerStore.document is null outside of Explorer, so this must
// bail out before touching it. This computed is read unconditionally by template `v-for`s (slot
// definitions evaluate regardless of whether those columns end up rendered), so it has to be the
// one place that's safe, rather than relying on every call site to guard itself.
const comparisonLabels = computed(() => {
	if (!isExplorer.value || !explorerStore.document) return [];

	const dateComparison = explorerStore.document.date_comparison;

	return [
		...[...dateComparison.periods]
			.sort((a, b) => a - b)
			.map((period) => `Y${period}`),
		...dateComparison.customPeriods.map((period) => period.name),
		dateComparison.isBudgetComparisonActive ? BUDGET_COMPARISON_LABEL : null,
	].filter((label): label is string => !!label);
});

// Each numeric column gets a fixed pixel width (below); the label column is left without a
// declared width, so under table-layout: fixed it simply absorbs whatever space is left over —
// staying spacious/right-aligned as today when there's room. minTableWidth is a floor equal to
// that same fixed sum, so the table only ever grows (and its wrapper scrolls horizontally)
// once there genuinely isn't enough room, instead of squeezing numeric columns unreadably thin.
//
// All of this (fixed-width value column, truncated metric-name title, table-width floor) only
// applies once comparison is actually active — with no comparison, the value column is the only
// numeric column and should just show the full metric name, same as before this feature existed.
const LABEL_COLUMN_MIN_WIDTH = 120;
const NUMERIC_COLUMN_WIDTH = 100;

const hasActiveComparison = computed(() => comparisonLabels.value.length > 0);

const minTableWidth = computed(() => {
	if (!isExplorer.value || !hasActiveComparison.value) return 0;

	const numericColumnCount = comparisonLabels.value.length + 1;

	return LABEL_COLUMN_MIN_WIDTH + numericColumnCount * NUMERIC_COLUMN_WIDTH;
});

const isAllValuesZero = (node: QuickFilterNodeWithPath) =>
	node.value !== null &&
	!node.value &&
	Object.values(node.comparisonValues ?? {}).every((value) => !value);

type BranchWithName = {
	branch: Branch;
	name: string;
};

const explorerClient = useApiClient("explorer");
const dashboardClient = useApiClient("dashboard");
const explorerStore = useExplorerStore();
const dashboardStore = useDashboardStore();
const hierarchyStore = useHierarchyStore();
const { abortController, abortRequestAndResetController } =
	useAbortController();
const {
	excludedBranches,
	toggleBranch,
	setBranch,
	isBranchExcluded,
	isBranchIndeterminate,
	clearBranches,
} = useExcludedBranch();

const props = defineProps<{
	activeFilter: ActiveFilterDTO;
	isExplorer?: boolean;
	documentUuid?: string;
}>();

const emit = defineEmits<{
	(e: "resize", height: number): void;
}>();

// ============================================================================
//  Hierarchy / Branch
// ============================================================================

const staticHierarchy = ref<QuickFilterNodeDTO | null>(null);

const hierarchyId = computed(
	() => activeFilter.value.dimension.hierarchy_definition_id
);
const hierarchyKey = computed(
	(): HierarchyKey => ({
		id: hierarchyId.value,
		name_style: explorerStore.document?.hierarchy_naming_style,
	})
);

/**
 * Loads the read-only `staticHierarchy` from the backend
 * This is then deep copied into `hierarchy`, which can be edited and stores the value for each hierarchy node
 */
const loadStaticHierarchy = async () => {
	if (!hierarchyStore.hasHierarchyNodes(hierarchyKey.value)) {
		isLoading.value = true;
	}
	errorMessage.value = null;

	staticHierarchy.value = await hierarchyStore.getOrLoadHierarchy(
		hierarchyKey.value,
		async () => {
			const response = isExplorer.value
				? await explorerClient.getHierarchy(
						explorerStore.document,
						hierarchyId.value
				  )
				: await dashboardClient.getHierarchy(null, hierarchyId.value);

			isLoading.value = false;

			if (response.isErr()) {
				errorMessage.value = hierarchyLoadErrorMessage(response.error);

				return null;
			}

			// Not ok and not an error means canceled: a newer request is already in flight, so
			// leave the message alone rather than reporting a failure that didn't happen.
			if (!response.isOk()) {
				return null;
			}

			const nodes = response.value as unknown as QuickFilterNodeDTO[];

			return nodes.length > 0 ? nodes[0] : null;
		}
	);
	syncHierarchy();
};

const { cloned: hierarchy, sync: syncHierarchy } = useCloned(staticHierarchy, {
	manual: true,
});

// vue-debounce's wrapper resolves as soon as the timer is *scheduled*, not when the debounced
// function actually runs — so awaiting `loadHierarchyNodeValues()` doesn't wait for the real
// fetch. `isSilentlyUpdating` is therefore toggled from inside the debounced callback itself,
// using this flag to know whether the run that's about to fire was a "silent" one.
let nextFetchIsSilent = false;

const debouncedLoadHierarchyNodeValues = debounce(async () => {
	if (nextFetchIsSilent) {
		isSilentlyUpdating.value = true;
		nextFetchIsSilent = false;
	}

	const getChildren = (
		node = currentNode.value,
		depth = activeFilter.value.values[0]?.selection_depth ?? 1,
		branch = currentBranch.value
	): QuickFilterNodeDTO[] => {
		if (depth <= 1) {
			return node?.children ?? [];
		}
		return (
			node?.children?.reduce(
				(acc, child, i) =>
					acc.concat(getChildren(child, depth - 1, branch.concat(i))),
				[]
			) ?? []
		);
	};

	if (!isExplorer.value) {
		isSilentlyUpdating.value = false;

		return;
	}

	abortRequestAndResetController();

	const response = await explorerClient.getHierarchyNodeValues(
		processedDocument(explorerStore.document),
		hierarchyId.value,
		true,
		abortController.value?.signal ?? null
	);

	if (response.isOk()) {
		let node = hierarchy.value;
		currentBranch.value.forEach((index) => (node = node.children[index]));

		getChildren(node).forEach((child) => {
			child.value = response.value[child.label]?.totalValue ?? 0;
			child.comparisonValues =
				response.value[child.label]?.comparisonValues ?? {};
		});

		// An empty response means nothing was fetched for this branch yet, not that every
		// comparison is unavailable, so only judge availability once there's something to judge against.
		if (Object.keys(response.value).length > 0) {
			unavailableComparisonLabels.value = getUnavailableComparisonLabels(
				comparisonLabels.value,
				response.value
			);
		}
		loadedComparisonLabels.value = new Set(comparisonLabels.value);
	}

	isSilentlyUpdating.value = false;
}, 60);

const loadHierarchyNodeValues = async (isSilent = false) => {
	if (isSilent) {
		nextFetchIsSilent = true;
	}
	debouncedLoadHierarchyNodeValues();
};

const currentBranch = ref<Branch>([]);

const activeFilterToCurrentBranch = () => {
	if (
		activeFilter.value.values[0].level === null &&
		activeFilter.value.values[0].label === null
	) {
		setFilterValues();
	}

	if (
		currentNode.value.level !== activeFilter.value.values[0].level ||
		currentNode.value.label !== activeFilter.value.values[0].label
	) {
		const branch = levelAndLabelToBranch(
			activeFilter.value.values[0].level,
			activeFilter.value.values[0].label,
			hierarchy.value
		);

		assert(branch !== null, "branch is null");

		currentBranch.value = branch ?? [];
	}
};

const currentNode = computed(() =>
	currentBranch.value.reduce(
		(current, index) => current.children[index],
		hierarchy.value
	)
);

// ============================================================================
//  Hierarchy Navigation
// ============================================================================

const levelAndLabelToBranch = (
	level: number | null,
	label: string | null,
	node
): Branch | null => {
	if (
		(level === null || node?.level === level) &&
		(label === null || node?.label === label)
	) {
		return [];
	}
	if (node?.level === level) {
		return null;
	}

	for (const [i, child] of node?.children?.entries() ?? []) {
		const branch = levelAndLabelToBranch(level, label, child);
		if (branch) {
			return [i].concat(branch);
		}
	}
	return null;
};

/**
 * The value of a hierarchy node is only calculated once its visible in the QFM.
 * This means that a user can quickly click through the menu, but they can also select nodes without a value.
 * This causes an invalid document, but can be mitigated by the "Back" button.
 * However, if the user is quickly clicking through multiple QFMs, this feedback gets really muddy,
 * so this option exists to reset all QFMs back to their root node
 */
const allHierarchiesToRoot = () => {
	if (!isExplorer.value) return;

	explorerStore.document.active_filters =
		explorerStore.document.active_filters.map((filter) => {
			if (
				filter.dimension?.hierarchy_definition_id !== null &&
				filter.dimension?.hierarchy_definition_id !== undefined
			) {
				// level and label === null -> root node of hierarchy
				filter.values[0].level = null;
				filter.values[0].label = null;
			}

			return filter;
		});
};

const isChildTraversable = (child: QuickFilterNodeWithPath): boolean => {
	if (isBranchExcluded(child.branch)) return false;

	// Some nodes are duplicated until depth -1
	// Prevent the user from traversing nodes that repeatedly have one child
	// Unless these nodes have different names, then it might be useful to know which child is available

	return (
		currentNode.value.children.length > 1 ||
		child.children.length > 1 ||
		(child.children[0]?.label && child.label !== child.children[0]?.label)
	);
};

const navigateToChild = (child: QuickFilterNodeWithPath): void => {
	if (!isChildTraversable(child)) return;
	currentBranch.value = child.branch;
	setSelectionDepth();
};

const navigateToParent = (): void => {
	// depth should be cleared as an easy way to escape slice view
	setSelectionDepth();
	if (!currentBranch.value.length) return;
	currentBranch.value.pop();
};

const setCurrentBranch = (branch: Branch): void => {
	setSelectionDepth();
	currentBranch.value = branch;
};

// ============================================================================
//  Derived Hierarchies (data table)
// ============================================================================

const getChildrenWithPath = (
	depth = activeFilter.value.values[0]?.selection_depth ?? 1,
	node = currentNode.value,
	branch = currentBranch.value
): QuickFilterNodeWithPath[] => {
	if (isBranchExcluded(branch)) {
		return [];
	}
	if (depth <= 1) {
		return (
			node?.children?.map((child, i) => ({
				...child,
				branch: branch.concat(i),
			})) ?? []
		);
	}
	return (
		node?.children?.reduce(
			(acc, child, i) =>
				acc.concat(getChildrenWithPath(depth - 1, child, branch.concat(i))),
			[]
		) ?? []
	);
};

const allChildren = computed(() => {
	if (hasInvalidDataSource.value) return [];

	return getChildrenWithPath();
});

const visibleChildren = computed(() => {
	const withValue = allChildren.value.filter(
		(child) => !isAllValuesZero(child)
	);
	const isLoadingValues = withValue.some((child) => child.value === null);

	const unique = [
		...new Map(withValue.map((i) => [i.label + "_" + i.level, i])).values(),
	];

	// When opening large hierarchies, it might freeze the browser.
	// With this hack we assume that for a loaded dataset not many (thousands) of items will be visible.
	if (isLoadingValues) {
		return unique.slice(0, 50);
	}

	return unique;
});

// ============================================================================
//  State (data table)
// ============================================================================

/**
 * Loading states are managed in 3 ways:
 *   1. Global loading state, when the static hierarchy isn't loaded in yet, and there's nothing to display (isLoading === true)
 *   2. Node-specific loading state, when the value of a hierarchy node is still being fetched from the backend (node.value === null)
 *   3. Comparison-column loading state, when a comparison period was just added and hasn't been
 *      included in a completed fetch yet (label not in loadedComparisonLabels)
 */
const isLoading = ref(true);
const errorMessage = ref<string | null>(null);
// True while values are being silently refetched because ANOTHER filter/quick filter changed
// something (not the initial load) — surfaced as a subtle animated indicator on the title.
const isSilentlyUpdating = ref(false);
const hoveredRowLabel = ref<string | null>(null);
// Whether the currently-hovered row's label is actually ellipsis-truncated — CSS can't expose
// this, so it's measured on hover and used to decide whether to show a tooltip with the full text.
const isLabelTruncated = ref(false);

const checkLabelTruncation = (event: MouseEvent) => {
	const target = event.currentTarget as HTMLElement;
	isLabelTruncated.value = target.scrollWidth > target.clientWidth;
};
const loadedComparisonLabels = ref<Set<string>>(new Set());
// Budget comparison specifically, when it's configured on the document but the backend never
// returned it for any node of the last completed fetch (it isn't supported for this hierarchy
// dimension) — rendered as "n/a". Period comparisons are always computable, so they render as 0.
const unavailableComparisonLabels = ref<Set<string>>(new Set());

const isOnly = (label: string) => {
	const includedChildren = visibleChildren.value.filter(
		(c) => !isBranchExcluded(c.branch)
	);

	return includedChildren.length === 1 && includedChildren[0].label === label;
};

const selectOnly = (branch: Branch) => {
	allChildren.value.forEach((c) => {
		setBranch(c.branch, true);
	});
	setBranch(branch, false);
};

const selectAll = () => {
	allChildren.value.forEach((c) => {
		setBranch(c.branch, false);
	});
};

const hasInvalidDataSource = computed(() =>
	isExplorer.value ? filterHasInvalidDataSource(props.activeFilter) : false
);

const sortBy = computed({
	get: (): SortItem[] => [
		{
			key: activeFilter.value?.sortBy ?? "",
			order: activeFilter.value?.sortDirection || false,
		},
	],
	set: (val: SortItem[]) => {
		const [sortBy, sortDirection] = [
			val[0]?.key ?? null,
			typeof val[0]?.order === "boolean" ? null : val[0]?.order,
		];
		activeFilter.value = { sortBy, sortDirection };
	},
});

const headerTitleLeft = computed((): BranchWithName[] => {
	let node = staticHierarchy.value;

	if (!node?.visibleLabel) {
		return [];
	}

	return [
		{
			branch: [],
			name: node.visibleLabel,
		},
	].concat(
		currentBranch.value.map((b, i) => {
			node = node.children[b];
			return {
				branch: currentBranch.value.slice(0, i + 1),
				name: node.visibleLabel,
			};
		})
	);
});

// ============================================================================
//  Header path collapsing
// ============================================================================

/**
 * Path segments are never squeezed individually. When the full path doesn't
 * fit the header, all ancestors collapse behind a "…" menu, leaving "… > last"
 * (the last segment CSS-truncates as floor). Widths are taken from a hidden
 * element rendering all segment names with the header's typography.
 */
const headerContainer = ref<HTMLElement | null>(null);
const depthChip = ref<{ $el: HTMLElement } | null>(null);
const pathMeasure = ref<HTMLElement | null>(null);

const containerWidth = ref(0);
const chipWidth = ref(0);
const segmentWidths = ref<number[]>([]);
const separatorWidth = ref(0);

// Chip right margin (mr-3) + sort icon reserve + rounding safety
const PATH_WIDTH_RESERVE = 12 + 24 + 8;

useResizeObserver(headerContainer, (entries) => {
	containerWidth.value = entries[0].contentRect.width;
});

const measurePathSegments = async () => {
	await nextTick();
	if (!pathMeasure.value) return;
	segmentWidths.value = Array.from(
		pathMeasure.value.querySelectorAll<HTMLElement>("[data-segment]")
	).map((segment) => segment.offsetWidth);
	separatorWidth.value =
		pathMeasure.value.querySelector<HTMLElement>("[data-separator]")
			?.offsetWidth ?? 0;
	chipWidth.value = depthChip.value?.$el?.offsetWidth ?? 0;
};

watch(headerTitleLeft, measurePathSegments, { immediate: true });
watch(() => props.activeFilter.values[0]?.selection_depth, measurePathSegments);

const isPathCollapsed = computed((): boolean => {
	const ancestors = headerTitleLeft.value.slice(0, -1);
	const widths = segmentWidths.value;

	const measurementsReady =
		containerWidth.value > 0 && widths.length === headerTitleLeft.value.length;

	if (ancestors.length === 0 || !measurementsReady) {
		return false;
	}

	const budget = containerWidth.value - chipWidth.value - PATH_WIDTH_RESERVE;
	const separator = separatorWidth.value;

	const fullWidth =
		widths.reduce((sum, width) => sum + width, 0) +
		separator * ancestors.length;

	return fullWidth > budget;
});

const vertical = computed(() => {
	// Currently only ever evaluated when isExplorer is true (its consumers are all gated on that
	// already), but guard explicitly rather than relying on that staying true everywhere.
	if (!isExplorer.value || !explorerStore.document) return undefined;

	if (explorerStore.document.view_tab === "graph") {
		return explorerStore.document?.vertical;
	} else {
		return explorerStore.document?.data_tables?.[0]?.metrics?.[0];
	}
});

const headerTitleRight = computed(() =>
	t(`properties.${vertical.value?.name_clean}.title`)
);

const headers = computed(() => [
	{
		title: headerTitleLeft.value,
		key: "visibleLabel",
	},
	...(isExplorer.value
		? comparisonLabels.value.map((label) => ({
				title: label,
				align: "end" as const,
				key: `comparison_${label}`,
				width: `${NUMERIC_COLUMN_WIDTH}px`,
				// The value lives at item.comparisonValues[label], not at a flat item[key], so
				// Vuetify's default getObjectValueByPath(item, key) sort can't find it — sortRaw
				// receives the full raw item instead, letting us read the nested value ourselves.
				sortRaw: (a: QuickFilterNodeWithPath, b: QuickFilterNodeWithPath) =>
					(a.comparisonValues?.[label] ?? 0) -
					(b.comparisonValues?.[label] ?? 0),
		  }))
		: []),
	isExplorer.value && {
		title: headerTitleRight.value,
		align: "end",
		key: "value",
		...(hasActiveComparison.value
			? { width: `${NUMERIC_COLUMN_WIDTH}px` }
			: {}),
	},
]);

const rowProps = (item: QuickFilterNodeWithPath) => ({
	class: [
		"row",
		isBranchExcluded(item.branch) ? "excluded-branch" : "included-branch",
	],
	onClick: () => navigateToChild(item),
	onContextmenu: (e) => {
		e.preventDefault();
		navigateToParent();
	},
	onMouseover: () => (hoveredRowLabel.value = item.label),
	onMouseleave: () => (hoveredRowLabel.value = null),
});

const checkboxProps = (item: QuickFilterNodeWithPath) => {
	const excluded = isBranchExcluded(item.branch);
	const indeterminate = isBranchIndeterminate(item.branch);
	const only = isOnly(item.label);
	const disabled = !excluded && !indeterminate && only;

	return {
		"model-value": !excluded,
		disabled,
		indeterminate,

		onClick: (e) => {
			if (indeterminate) {
				// indeterminate -> toggled
				e.preventDefault();
				setBranch(item.branch, false, true);
			} else {
				toggleBranch(item.branch);
			}
		},
	};
};

// ============================================================================
//  State (misc)
// ============================================================================

const { cloned: previousDocument, sync: syncDocument } = useCloned(
	explorerStore.document,
	{
		manual: true,
	}
);

const hasHierarchyDefinitionChanged = (): boolean => {
	const originalFilterConfiguration =
		previousDocument.value.active_filters.find(
			(filter) => filter.dimension.id === activeFilter.value.dimension.id
		);

	return (
		originalFilterConfiguration.dimension.hierarchy_definition_id !==
		activeFilter.value.dimension.hierarchy_definition_id
	);
};

const hasHierarchyNamingStyleChanged = () => {
	return (
		previousDocument.value.hierarchy_naming_style !==
		explorerStore.document.hierarchy_naming_style
	);
};

const isExplorer = computed(() => props.isExplorer ?? false);

const activeFilter = computed({
	get: () => props.activeFilter,
	set: (patch: Partial<ActiveFilterDTO>) => {
		const newFilter = { ...props.activeFilter, ...patch };

		if (JSON.stringify(newFilter) === JSON.stringify(props.activeFilter)) {
			return;
		}

		if (isExplorer.value) {
			if (useExplorerStore().document.uuid !== props.documentUuid) {
				return;
			}

			explorerStore.APPLY_FILTER(newFilter, "edit");
		} else {
			dashboardStore.applyFilter(newFilter, "edit");
		}
	},
});

const resetHierarchy = async (resetCurrentBranch = false) => {
	if (resetCurrentBranch) {
		currentBranch.value = [];
	}
	await loadStaticHierarchy();
	activeFilterToCurrentBranch();
	activeFilterToExcludedBranches();
	isLoading.value = false;
};

const setFilterValues = () => {
	activeFilter.value = {
		values: [
			{
				id: null,
				label: currentNode.value.label,
				selection_depth: activeFilter.value.values[0].selection_depth,
				sort_order: null,
				filter_period: null,
				model: currentNode.value.model,
				level: currentNode.value.level,
				excluded_branches: excludedBranchObjects.value,
			},
		],
	};
};

const setSelectionDepth = (selection_depth = 1) => {
	activeFilter.value = {
		values: [
			{
				...activeFilter.value.values[0],
				selection_depth,
			},
		],
	};
};

const excludedBranchObjects = computed((): ExcludedHierarchyFilterValueDTO[] =>
	excludedBranches.value.map((excludedBranch) => {
		let node = hierarchy.value;

		excludedBranch.forEach((index) => {
			node = node.children[index];
		});

		return {
			level: node.level,
			label: node.label,
		};
	})
);

const activeFilterToExcludedBranches = () => {
	clearBranches();
	activeFilter.value.values[0].excluded_branches.forEach((branch) =>
		setBranch(
			levelAndLabelToBranch(branch.level, branch.label, hierarchy.value),
			true
		)
	);
};

// ============================================================================
//  Watchers
// ============================================================================

// Update filters when changes are made
watch(currentNode, async (nv, ov) => {
	if (ov) {
		setFilterValues();
		await loadHierarchyNodeValues();
	}
});
watch(excludedBranchObjects, async (nv, ov) => {
	if (ov) {
		setFilterValues();
		await loadHierarchyNodeValues();
	}
});

/**
 * While the QFM is open, the regular FM is also able to make changes (parent node, excluded branches).
 * These also need to be acted on
 */
watch(
	() => ({
		label: activeFilter.value.values[0]?.label,
		level: activeFilter.value.values[0]?.level,
	}),
	async (nv, ov) => {
		if (JSON.stringify(nv) !== JSON.stringify(ov)) {
			activeFilterToCurrentBranch();
		}
		await loadHierarchyNodeValues();
	}
);
watch(
	() => activeFilter.value.values[0]?.excluded_branches,
	async () => {
		activeFilterToExcludedBranches();
		await loadHierarchyNodeValues();
	}
);

/**
 * If any changes (aside from the current filter or UUID) are made,the values in the QFM need to be refetched.
 * As the hierarchy naming style directly influences the labels of the nodes, this requires a full refetch of the `staticHierarchy`.
 * Same applies for the hierarchy definition, however this can also cause the nodes to now be in different locations, so the current branch is reset to the root branch
 */
watch(
	() => explorerStore.explorerState.lastUpdated.getTime(),
	async () => {
		// explorerStore.document is null outside of Explorer (dashboards have no equivalent), so
		// there's nothing to diff against — and no silent-refetch signal is meaningful there anyway,
		// since loadHierarchyNodeValues() already no-ops when !isExplorer.
		if (
			isExplorer.value &&
			explorerStore.document &&
			hasDocumentChanged(
				explorerStore.document,
				previousDocument.value,
				activeFilter.value.dimension?.id
			)
		) {
			if (hasHierarchyNamingStyleChanged()) {
				await resetHierarchy();
			} else if (hasHierarchyDefinitionChanged()) {
				await resetHierarchy(true);
			}
			await loadHierarchyNodeValues(true);
			syncDocument();
		}
	},
	{ deep: true }
);

watch(visibleChildren, () => {
	if (
		visibleChildren.value.length &&
		!visibleChildren.value.some((child) => child.value === null)
	) {
		emit("resize", visibleChildren.value?.length);
	}
});

// ============================================================================
//  Lifetime
// ============================================================================

onMounted(async () => {
	await resetHierarchy();
	await loadHierarchyNodeValues();
});
</script>

<template>
	<div
		class="w-100 px-2 overflow-y-scroll overflow-x-auto"
		:style="{ '--min-table-width': `${minTableWidth}px` }"
		@contextmenu.prevent
	>
		<v-data-table
			v-model:sort-by="sortBy"
			:headers="headers"
			:items="visibleChildren"
			:loading="isLoading"
			:items-per-page="-1"
			density="compact"
			class="w-100 table"
			hide-default-footer
		>
			<template #no-data>
				<div class="no-data" @contextmenu.prevent="navigateToParent">
					<div v-if="hasInvalidDataSource">
						The quick filter menu isn't supported for this vertical
					</div>
					<template v-else>
						{{ errorMessage ?? "No data available" }}
						<template v-if="currentBranch.length > 0">
							<v-btn
								size="small"
								height="26"
								prepend-icon="mdi-arrow-left"
								@click="navigateToParent"
							>
								Back
							</v-btn>
							<v-btn
								size="small"
								height="26"
								color="secondary"
								prepend-icon="mdi-arrow-up"
								@click="allHierarchiesToRoot"
							>
								Top of hierarchy
							</v-btn>
						</template>
					</template>
				</div>
			</template>

			<template v-slot:header.visibleLabel="{ column }">
				<div ref="headerContainer" class="d-flex align-center ml-n4">
					<!-- menu with selection depth -->
					<v-menu transition="slide-y-transition">
						<template v-slot:activator="{ props }">
							<v-chip
								ref="depthChip"
								color="info"
								class="px-2 mr-3 flex-grow-0 flex-shrink-0"
								style="height: 26px"
								v-bind="props"
							>
								{{ activeFilter.values[0]?.selection_depth }}
								<i class="ml-2 fa-regular fa-arrow-down-short-wide"></i>
							</v-chip>
						</template>
						<v-list>
							<v-list-item
								v-for="index in currentNode.level"
								:key="index"
								@click="setSelectionDepth(index)"
							>
								<v-list-item-title>{{ index }}</v-list-item-title>
							</v-list-item>
						</v-list>
					</v-menu>

					<!-- traversable part of path, shouldn't trigger sorting -->
					<div
						v-if="headerTitleLeft.length"
						class="header-path"
						@click.stop
						@contextmenu.prevent.stop
					>
						<template v-if="!isPathCollapsed">
							<template
								v-for="path in headerTitleLeft.slice(0, -1)"
								:key="path.name"
							>
								<span
									class="text-decoration-underline"
									@click="setCurrentBranch(path.branch)"
									data-testid="quick-filter-breadcrumb"
								>
									{{ path.name }}
								</span>
								<span class="px-1"> > </span>
							</template>
						</template>
						<template v-else>
							<v-menu transition="slide-y-transition">
								<template v-slot:activator="{ props }">
									<span class="text-decoration-underline" v-bind="props">
										…
									</span>
								</template>
								<v-list class="path-menu-list" density="compact">
									<v-list-item
										v-for="(path, i) in headerTitleLeft.slice(0, -1)"
										:key="i"
										@click="setCurrentBranch(path.branch)"
									>
										<v-list-item-title>{{ path.name }}</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-menu>
							<span class="px-1"> > </span>
						</template>
					</div>

					<!-- non-traversable part of path, should trigger sorting -->
					<custom-table-header header-key="visibleLabel" :sort-by="sortBy">
						<span v-if="!headerTitleLeft.length">
							{{ t(`properties.${activeFilter.dimension.name_clean}.title`) }}
						</span>
						<span class="text-truncate">
							{{ headerTitleLeft[headerTitleLeft.length - 1]?.name ?? "" }}
						</span>
						<v-fade-transition>
							<v-progress-linear
								v-if="isSilentlyUpdating"
								indeterminate
								height="3"
								style="width: 100px"
								bg-color="transparent"
								class="mx-5"
							/>
						</v-fade-transition>
					</custom-table-header>

					<!-- invisible copy of all segments, used to measure their widths -->
					<div ref="pathMeasure" class="header-path-measure" aria-hidden="true">
						<span v-for="(path, i) in headerTitleLeft" :key="i" data-segment>
							{{ path.name }}
						</span>
						<span class="px-1" data-separator> > </span>
					</div>
				</div>
			</template>

			<template v-slot:header.value="{ column }">
				<custom-table-header header-key="value" :sort-by="sortBy" align="end">
					<span
						v-tooltip="hasActiveComparison ? column.title : false"
						:class="{ 'text-truncate': hasActiveComparison }"
						:style="hasActiveComparison ? 'min-width: 3ch' : ''"
						>{{ column.title }}</span
					>
				</custom-table-header>
			</template>

			<template
				v-for="label in comparisonLabels"
				:key="label"
				#[`header.comparison_${label}`]="{ column }"
			>
				<custom-table-header
					:header-key="`comparison_${label}`"
					:sort-by="sortBy"
					align="end"
				>
					<span
						v-tooltip="column.title"
						class="text-truncate"
						style="max-width: 4ch"
						>{{ column.title }}</span
					>
				</custom-table-header>
			</template>

			<template #item.visibleLabel="{ item }">
				<div v-bind="rowProps(item)">
					<div
						class="d-flex align-center cursor-default h-100 pl-1"
						@click.stop
						@contextmenu.prevent.stop
					>
						<v-checkbox-btn
							v-bind="checkboxProps(item)"
							density="comfortable"
						/>
					</div>
					<span
						v-tooltip="isLabelTruncated ? item.visibleLabel : false"
						class="text-truncate"
						style="min-width: 0"
						@mouseenter="checkLabelTruncation"
						>{{ item.visibleLabel }}</span
					>
					<v-fade-transition>
						<div
							v-if="hoveredRowLabel === item.label && allChildren.length > 1"
							class="ml-4 flex-shrink-0"
						>
							<v-btn
								v-if="isOnly(item.label)"
								size="x-small"
								height="24"
								width="48"
								variant="text"
								class="only-all"
								@click.stop="selectAll"
							>
								All
							</v-btn>
							<v-btn
								v-else
								size="x-small"
								height="24"
								width="48"
								variant="text"
								class="only-all"
								@click.stop="selectOnly(item.branch)"
							>
								Only
							</v-btn>
						</div>
					</v-fade-transition>
				</div>
			</template>

			<template #item.value="{ item }">
				<div v-bind="rowProps(item)" class="justify-end pr-4">
					<v-spacer />
					<template v-if="isExplorer">
						<v-progress-linear
							v-if="item?.value === null"
							height="3"
							style="width: 30px"
							bg-color="transparent"
							indeterminate
						/>
						<div
							v-else
							:class="{ 'excluded-branch': isBranchExcluded(item.branch) }"
							style="user-select: none"
						>
							{{ formatNumber(item.value, vertical.format) }}
						</div>
					</template>
				</div>
			</template>

			<template
				v-for="label in comparisonLabels"
				:key="label"
				#[`item.comparison_${label}`]="{ item }"
			>
				<div v-bind="rowProps(item)" class="justify-end pr-4">
					<v-spacer />
					<div
						:class="{ 'excluded-branch': isBranchExcluded(item.branch) }"
						style="user-select: none"
					>
						<QuickFilterComparisonValueCell
							:state="
								getComparisonCellState(
									item,
									label,
									loadedComparisonLabels,
									unavailableComparisonLabels
								)
							"
							:format="vertical.format"
						/>
					</div>
				</div>
			</template>
		</v-data-table>
	</div>
</template>

<style scoped lang="scss">
.table :deep(table) {
	table-layout: fixed;
	width: 100%;
	min-width: var(--min-table-width, 0);
}

.table {
	font-size: 13px;
}

.no-data {
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
	gap: 0.5rem;
	width: 100%;
	height: 100%;
}

.row {
	display: flex;
	align-items: center;
	width: calc(100% + 2rem);
	height: 100%;
	margin: 0 -1rem;
	user-select: none;
}

.excluded-branch {
	color: rgba(var(--v-theme-on-surface), 0.5);
}
.included-branch {
	cursor: pointer;
}

.header-path {
	min-width: 0;
	overflow: hidden;
	white-space: nowrap;
}

.path-menu-list {
	padding-block: 2px;

	:deep(.v-list-item) {
		min-height: 0;
		padding-block: 4px;
		padding-inline: 10px;
	}

	:deep(.v-list-item-title) {
		font-size: 13px;
		font-weight: 600;
		line-height: 1.3;
	}
}

.header-path-measure {
	position: fixed;
	top: -9999px;
	left: 0;
	visibility: hidden;
	white-space: nowrap;
	pointer-events: none;

	span {
		display: inline-block;
	}
}

.only-all {
	color: rgb(var(--v-theme-on-surface));
	text-transform: capitalize;
	font-weight: 400;
	font-size: 12px;
}
</style>
